import {CircularProgress, createStyles, makeStyles, Theme, Typography} from "@material-ui/core";
import Colors from "Constants/Colors";
import { FPSOArea } from "Models/FPSOArea";
import { FC, useCallback, useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { BasePieChart } from "Views/Components/FpsoOverview/Graphs/BasePieChart";
import {useAuth} from "../../../../auth";
import {CarUtils, RatioResult} from "../../../../Utils/CarUtils";

interface Props {
  readonly fpsoArea: FPSOArea;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    errorContainer: {
      padding: theme.spacing(2),
    },
  }),
);

export const OpenCarPerformanceIndicatorRatioChart: FC<Props> = ({
  fpsoArea,
}) => {
  const { t } = useTranslation("cars");
  const { auth } = useAuth();
  const classes = useStyles();

  const [chartData, setChartData] = useState<Plotly.Data[]>([]);
  const [data, setData] = useState<RatioResult[]>([]);
  const [loading, setLoading] = useState(true);

  const getData = useCallback(async () => {
    setLoading(true);
    const token = await auth?.getToken();
    const ratios = await CarUtils.GetRatio(
      "GetOpenCarPerformanceIndicatorRatio",
      fpsoArea.id,
      `bearer ${token}`);

    setData(ratios?.data);
    setLoading(false);
  }, [fpsoArea.id, auth]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [fpsoArea.id]);

  const handleCarRatioDataReceived = useCallback(() => {
    const keys: string[] = [];
    const values: number[] = [];
    const sectionColors: string[] = [];

    data.forEach((ratio) => {
      keys.push(
        ratio.result === "Grey"
          ? t("noDeadline")
          : ratio.result === "Blue"
            ? t("inProgress")
            : ratio.result === "Yellow"
              ? t("deadlineShifted")
              : ratio.result === "Red"
                ? t("deadlineExceeded")
                : "",
      );
      values.push(ratio.ratio);
      sectionColors.push(
        ratio.result === "Grey"
          ? Colors.PERFORMANCE_INDICATOR_GREY
          : ratio.result === "Blue"
            ? Colors.PERFORMANCE_INDICATOR_BLUE
            : ratio.result === "Yellow"
              ? Colors.PERFORMANCE_INDICATOR_YELLOW
              : ratio.result === "Red"
                ? Colors.PERFORMANCE_INDICATOR_RED
                : "",
      );
    });

    const openCarPerformanceIndicatorTrace = {
      values,
      labels: keys,
      type: "pie",
      name: "Open Car Performance Indicator ratio",
      textfont: {
        family: '"Red Hat Display", sans-serif',
        size: 20,
        color: Colors.DARK_GREY,
      },
      textinfo: "value",
      showlegend: true,
      sort: false,
      marker: {
        colors: sectionColors,
        line: {
          color: Colors.WHITE,
          width: [4, 4, 4, 4],
        },
      },
    };

    const plotData: Plotly.Data[] = [];
    plotData.push(openCarPerformanceIndicatorTrace as Plotly.Data);
    setChartData(plotData);

    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    handleCarRatioDataReceived();
    // eslint-disable-next-line
  }, [data]);

  const titletext = t("openCarPerformanceIndicatorRatio");

  if (loading) {
    return (
      <CircularProgress />
    );
  }

  if (data.length !== 0) {
    return <BasePieChart data={chartData} titleText={titletext} />;
  }

  if (data.length === 0) {
    return (
      <div className={classes.errorContainer}>
        <Typography>
          {t("errors:carOpenPerformanceIndicatorRatioChartFetchNoData")}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.errorContainer}>
      {" "}
      <Typography>
        {t("errors:carPerformanceIndicatorRatioChartFetchError")}
      </Typography>
    </div>
  );
};
